/* eslint-disable max-lines-per-function */
import React, { type FC, useMemo } from 'react';
import type { AttachesItem } from 'reactApp/modules/attaches/attaches.types';
import type { CloudFile } from 'reactApp/modules/storage/storage.types';
import { EditorRestrictionTooltipWrapper } from 'reactApp/ui/EditorRestriction/EditorRestrictionTooltip/EditorRestrictionTooltipWrapper';
import { CloseMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/CloseMenuButton/CloseMenuButton';
import { DownloadMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/DownloadMenuButton/DownloadMenuButton';
import { EditMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/EditMenuButton/EditMenuButton';
import { MenuInfo } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuInfo/MenuInfo';
import { MenuLogo } from 'reactApp/ui/ReactViewer/ViewerMenu/MenuLogo/MenuLogo';
import { MoreMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/MoreMenuButton/MoreMenuButton';
import { PdfEditMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/PdfEditMenuButton/PdfEditMenuButton';
import { ShareMenuButton } from 'reactApp/ui/ReactViewer/ViewerMenu/ShareMenuButton/ShareMenuButton';
import type { ViewerMenuProps } from 'reactApp/ui/ReactViewer/ViewerMenu/ViewerMenu';
import { ViewerMenuCenter, ViewerMenuEnd, ViewerMenuStart, ViewerMenuWrapper } from 'reactApp/ui/ReactViewer/ViewerMenu/ViewerMenu.blocks';
import { Zoomer } from 'reactApp/ui/ReactViewer/ViewerMenu/Zoomer/Zoomer';

import { getMoreItems } from './helpers';

interface Props extends ViewerMenuProps {
    file: CloudFile | AttachesItem;
}

export const MainMenu: FC<Props> = ({
    file,
    showLogo,
    zoom,
    decreaseZoom,
    increaseZoom,
    isArchiveItem,
    onPublish,
    showDownload,
    onDownload,
    showEdit,
    onEdit,
    normalizedOvidiusEnabled,
    showPdfEdit,
    onPdfEdit,
    onPdfCopyEdit,
    showImageEdit,
    onImageEdit,
    showClose,
    onClose,
    isEditingDisabled,
    showPublish,
    ...rest
}) => {
    const isSingleEdit = normalizedOvidiusEnabled ? showEdit && normalizedOvidiusEnabled : showEdit;

    const moreItems = useMemo(() => getMoreItems({ file, ...rest }), [file, rest]);

    return (
        <ViewerMenuWrapper>
            <ViewerMenuStart>
                {showLogo && <MenuLogo />}
                <MenuInfo file={file} />
            </ViewerMenuStart>
            <ViewerMenuCenter>{zoom && <Zoomer value={zoom} decrease={decreaseZoom} increase={increaseZoom} />}</ViewerMenuCenter>
            <ViewerMenuEnd>
                {isArchiveItem ? (
                    <>
                        {showDownload && (
                            <DownloadMenuButton
                                file={file}
                                onClick={() => {
                                    onDownload(file);
                                }}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {showPublish && (
                            <ShareMenuButton
                                onClick={() => {
                                    onPublish(file);
                                }}
                            />
                        )}
                        {showDownload && (
                            <DownloadMenuButton
                                file={file}
                                onClick={() => {
                                    onDownload(file);
                                }}
                            />
                        )}
                        {isSingleEdit ? (
                            <EditorRestrictionTooltipWrapper isEditingDisabled={isEditingDisabled}>
                                <EditMenuButton
                                    onClick={() => {
                                        if (isEditingDisabled) {
                                            return;
                                        }
                                        onEdit(file);
                                    }}
                                />
                            </EditorRestrictionTooltipWrapper>
                        ) : (
                            <>
                                {showPdfEdit && (
                                    <PdfEditMenuButton
                                        onPdfEdit={() => {
                                            onPdfEdit(file);
                                        }}
                                        onPdfCopyEdit={() => {
                                            onPdfCopyEdit(file);
                                        }}
                                    />
                                )}
                            </>
                        )}
                        {showImageEdit && (
                            <EditMenuButton
                                onClick={() => {
                                    onImageEdit(file);
                                }}
                            />
                        )}
                        <MoreMenuButton list={moreItems} />
                    </>
                )}
                {showClose && (
                    <CloseMenuButton
                        onClick={() => {
                            onClose(file);
                        }}
                    />
                )}
            </ViewerMenuEnd>
        </ViewerMenuWrapper>
    );
};
